import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/table";
import { Heading } from "../components/heading";
import { Button } from "../components/button";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { BSService } from "../models/service";
import { formatCurrency } from "../helpers/currency.helpers";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "../components/dialog";
import { Fieldset, Label } from "../components/fieldset";
import * as Headless from "@headlessui/react";
import { Input } from "../components/input";
import { Textarea } from "../components/textarea";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownMenu,
} from "../components/dropdown";
import { EllipsisHorizontalIcon, PlusIcon } from "@heroicons/react/20/solid";
import EmptyState from "./components/empty-state";
import { useTableSort } from '../hooks/useTableSort';
import { useColumnVisibility, ColumnConfig } from '../hooks/useColumnVisibility';
import { useTableSearch } from '../hooks/useTableSearch';
import { ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { Switch, SwitchField } from "../components/switch";
import { InlineSwitch } from "./components/inline-switch";

export default function Services() {
  const collectionName = "services";
  const emptyObject: BSService = {
    id: "",
    productServiceName: "",
    price: 0,
    salesDescription: "",
    public: false,
    needsGroom: false
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [newObject, setNewObject] = useState<BSService>(emptyObject);
  const [objects, setObjects] = useState<BSService[]>([]);

  useEffect(() => {
    const fetchData = () => {
      const q = query(collection(db, collectionName));
      onSnapshot(q, (querySnapshot) => {
        const objectsArray: BSService[] = [];
        querySnapshot.forEach((doc) => {
          const serviceData = doc.data() as BSService;
          objectsArray.push({ ...serviceData, id: doc.id });
        });
        setObjects(objectsArray);
      });
    };

    fetchData();
  }, []);

  const saveObject = async () => {
    if (newObject.id === "") {
      const newDocRef = doc(collection(db, collectionName));
      newObject.id = newDocRef.id;
      await setDoc(newDocRef, newObject);
    } else {
      await updateDoc(doc(db, collectionName, newObject.id), newObject);
    }

    setIsDialogOpen(false);
    resetForm();
  };

  const editObject = (object: BSService) => {
    setNewObject(object);
    setIsDialogOpen(true);
  };

  const deleteObject = async (object: BSService) => {
    await deleteDoc(doc(db, collectionName, object.id));
  };

  const resetForm = () => {
    setNewObject(emptyObject);
  };

  // Add columns configuration
  const columns: ColumnConfig<BSService>[] = [
    { field: 'productServiceName', label: 'Product Service Name', defaultVisible: true },
    { field: 'price', label: 'Price', defaultVisible: true },
    { field: 'salesDescription', label: 'Sales Description', defaultVisible: true },
    { field: 'public', label: 'Public', defaultVisible: true },
    { field: 'needsGroom', label: 'Needs Groom', defaultVisible: false },
    { field: 'actions', label: 'Actions', defaultVisible: true }
  ];

  // Add hooks
  const { sortField, sortDirection, handleSort, getSortedData } = useTableSort<BSService>('productServiceName');
  const { toggleColumn, isColumnVisible } = useColumnVisibility<BSService>(columns);
  const { searchQuery, setSearchQuery, getFilteredData } = useTableSearch<BSService>();

  // Add SortableHeader component
  const SortableHeader = ({ field, children }: { field: keyof BSService, children: React.ReactNode }) => {
    const isCurrent = sortField === field;
    return (
      <TableHeader 
        className="cursor-pointer select-none group hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <span className="inline-flex">
            {isCurrent ? (
              sortDirection === 'asc' ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )
            ) : (
              <ChevronUpDownIcon className="w-4 h-4 opacity-0 group-hover:opacity-50" />
            )}
          </span>
        </div>
      </TableHeader>
    );
  };

  // Add filtered data function
  const getFilteredServices = () => {
    const filtered = getFilteredData(objects, (service) => [
      service.productServiceName || '',
      formatCurrency(service.price),
      service.salesDescription || ''
    ]);

    return getSortedData(filtered);
  };

  const updateServicePublic = async (service: BSService, isPublic: boolean) => {
    try {
      await updateDoc(doc(db, 'services', service.id), {
        public: isPublic
      });
    } catch (error) {
      console.error('Error updating service public status:', error);
    }
  };

  const updateServiceNeedsGroom = async (service: BSService, needsGroom: boolean) => {
    try {
      await updateDoc(doc(db, 'services', service.id), {
        needsGroom
      });
    } catch (error) {
      console.error('Error updating service needs groom status:', error);
    }
  };

  return (
    <div>
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>Services</Heading>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Input
              type="search"
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-[250px]"
            />
          </div>
          <Dropdown>
            <DropdownButton>
              <EyeIcon className="w-5 h-5 mr-2" />
              Columns
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              {columns.map(({ field, label }) => (
                <DropdownItem
                  key={field}
                  onClick={() => toggleColumn(field)}
                >
                  <div className="flex items-center gap-2">
                    {isColumnVisible(field) ? (
                      <EyeIcon className="w-4 h-4" />
                    ) : (
                      <EyeSlashIcon className="w-4 h-4" />
                    )}
                    {label}
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
          >
            Add Service
          </Button>
        </div>
      </div>

      {objects.length > 0 ? (
        <Table
          dense
          striped
          className="[--gutter:theme(spacing.6)] sm:[--gutter:theme(spacing.8)]"
        >
          <TableHead>
            <TableRow>
              {columns.map(({ field, label }) => 
                isColumnVisible(field) && (
                  field === 'actions' ? (
                    <TableHeader key={field}>Actions</TableHeader>
                  ) : (
                    <SortableHeader key={field} field={field}>
                      {label}
                    </SortableHeader>
                  )
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredServices().map((object) => (
              <TableRow key={object.id}>
                {isColumnVisible('productServiceName') && (
                  <TableCell className="font-medium">{object.productServiceName}</TableCell>
                )}
                {isColumnVisible('price') && (
                  <TableCell>{formatCurrency(object.price)}</TableCell>
                )}
                {isColumnVisible('salesDescription') && (
                  <TableCell>{object.salesDescription}</TableCell>
                )}
                {isColumnVisible('public') && (
                  <TableCell>
                    <InlineSwitch
                      checked={object.public}
                      onChange={(checked) => updateServicePublic(object, checked)}
                    />
                  </TableCell>
                )}
                {isColumnVisible('needsGroom') && (
                  <TableCell>
                    <InlineSwitch
                      checked={object.needsGroom}
                      onChange={(checked) => updateServiceNeedsGroom(object, checked)}
                    />
                  </TableCell>
                )}
                {isColumnVisible('actions') && (
                  <TableCell>
                    <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                      <Dropdown>
                        <DropdownButton plain aria-label="More options">
                          <EllipsisHorizontalIcon />
                        </DropdownButton>
                        <DropdownMenu anchor="bottom end">
                          <DropdownItem onClick={() => editObject(object)}>
                            Edit
                          </DropdownItem>
                          <DropdownDivider />
                          <DropdownItem onClick={() => deleteObject(object)}>
                            Delete
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          title={"No Services"}
          description={"Add new services to manage them here"}
        >
          <PlusIcon className="w-10 h-10" />
        </EmptyState>
      )}

      <Dialog open={isDialogOpen} onClose={setIsDialogOpen}>
        <DialogTitle>New Service</DialogTitle>
        <DialogBody>
          <Fieldset>
            <div
              data-slot="control"
              className="grid grid-cols-1 items-center gap-x-4 gap-y-6 sm:grid-cols-3"
            >
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Name</Label>
                <Input
                  type="text"
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      productServiceName: e.target.value,
                    })
                  }
                  value={newObject.productServiceName}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  name="name"
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Price</Label>
                <Input
                  type="number"
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      price: e.target.value as any,
                    })
                  }
                  value={newObject.price}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  name="price"
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Description</Label>
                <Textarea
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      salesDescription: e.target.value,
                    })
                  }
                  value={newObject.salesDescription}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  name="description"
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Public</Label>
                <SwitchField>
                  <Switch color="green" checked={newObject.public} onChange={(e) => setNewObject({ ...newObject, public: e })} />
                </SwitchField>
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Needs Groom</Label>
                <SwitchField>
                  <Switch color="green" checked={newObject.needsGroom} onChange={(e) => setNewObject({ ...newObject, needsGroom: e })} />
                </SwitchField>
              </Headless.Field>
            </div>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => saveObject()}>{ newObject.id === '' ? 'Add' : 'Save' }</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
